/**
 * PlanSummary - High-level overview of a generated plan
 *
 * Shows the transformation from prompt to structured plan:
 * - Original prompt
 * - Processing status/progress
 * - Summary stats (tasks, events, commands, story points)
 * - Tech stack
 * - Quick actions (export, refine)
 */
import React from 'react';
import {
  Card,
  Button,
  Text,
  LoadingSpinner,
  tokens,
} from '../design-system';
import { usePlanContext } from '../hooks/api/usePlanContext';
import { useProcessingStage } from '../hooks/useProcessingStage';
import LiveActivityFeed from './LiveActivityFeed';

const PlanSummary = ({
  task,
  taskTree,
  onRefine,
  onExport,
  onResume,
  isReprocessing = false
}) => {
  // Derive processing state, then use it to drive polling
  const prevProcessingRef = React.useRef(false);
  const { data: planContext, isLoading: contextLoading } = usePlanContext(task?.task_id, {
    // Poll every 3s while processing so we catch completion reliably
    refetchInterval: prevProcessingRef.current ? 3000 : false,
  });
  // Use plan status from context API (Plans have statuses, Tasks do not)
  // When context is still loading, assume processing is true if we're still waiting for data
  const planStatus = planContext?.plan_status;
  const isProcessing = isReprocessing ||
                       planContext?.is_processing ||
                       planStatus === 'submitted' ||
                       planStatus === 'processing' ||
                       // If context is loading and we don't have data yet, assume processing
                       // (will be updated when context loads)
                       (contextLoading && !planContext);
  // Keep ref in sync so polling stops once we detect completion
  prevProcessingRef.current = isProcessing;

  return (
    <>
      <Card style={{
        ...styles.container,
        ...(isProcessing && { display: 'flex', flexDirection: 'column' }),
      }}>
        {/* Original Prompt - Hidden during processing */}
        {!isProcessing && (
          <div style={styles.promptSection}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2], marginBottom: tokens.spacing[3] }}>
              <span style={{ fontSize: '24px' }}>💭</span>
              <Text style={styles.promptLabel}>Your Plan Vision</Text>
            </div>
            <div style={styles.promptText} className="custom-scrollbar">
              <Text style={{ fontStyle: 'italic', fontSize: tokens.typography.fontSize.lg[0] }}>
                "{task?.description}"
              </Text>
            </div>
          </div>
        )}

        {/* Processing Status */}
        {isProcessing && (
          <ProcessingStatus
            task={task}
            taskTree={taskTree}
            context={planContext}
            contextLoading={contextLoading}
            onResume={onResume}
            isReprocessing={isReprocessing}
            planStatus={planStatus}
          />
        )}

        {/* Actions (when not processing) */}
        {!isProcessing && (
          <div style={styles.actionsRow}>
            <Button
              variant="outline"
              size="md"
              onClick={onExport}
            >
              Export
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={onRefine}
            >
              Refine Plan
            </Button>
          </div>
        )}
      </Card>
    </>
  );
};

/**
 * Processing status with compact header and feed-first layout
 */
const ProcessingStatus = ({ task, taskTree, context, contextLoading, onResume, isReprocessing = false, planStatus }) => {
  const {
    stage: currentStage,
    childTaskCount: wsTaskCount,
    hasFeatures: wsHasFeatures,
    eventModelingStarted,
    decompositionCompleted,
    repoAnalysisStarted,
    discoveryStarted,
    ideaSpecStarted,
    currentPhase,
    progressMessage,
    progressMessages,
    isProcessing: hookIsProcessing,
  } = useProcessingStage(task?.task_id, task, taskTree, isReprocessing);

  // Detect if processing was interrupted
  const hasApiData = (task?.children_count || 0) > 1 ||
                     (task?.context?.user_stories?.length || 0) > 0 ||
                     (task?.metadata?.user_stories?.length || 0) > 0;
  const hasActiveWsProgress = wsTaskCount > 0 || wsHasFeatures || eventModelingStarted || decompositionCompleted || repoAnalysisStarted || discoveryStarted || ideaSpecStarted;
  const isInterrupted = planStatus === 'submitted' && hasApiData && !hasActiveWsProgress;

  if (isInterrupted) {
    return (
      <div style={styles.processingSection}>
        <div style={styles.interruptedHeader}>
          <Text style={styles.interruptedIcon}>⚠️</Text>
          <Text style={styles.processingText}>Processing was interrupted</Text>
        </div>
        <Text style={styles.interruptedMessage}>
          This plan was partially processed but didn't complete. You can resume processing to finish generating the event model.
        </Text>
        {onResume && (
          <Button
            variant="primary"
            onClick={onResume}
            style={{ marginTop: tokens.spacing[4] }}
          >
            Resume Processing
          </Button>
        )}
      </div>
    );
  }

  const engineeringStarted = currentPhase === 'engineering' || wsHasFeatures || wsTaskCount > 0;
  const isIdeaPhase = !engineeringStarted;

  const headerText = isIdeaPhase
    ? 'Creating your plan...'
    : 'Building your plan...';

  return (
    <div style={styles.processingSection}>
      {/* Compact Progress Header */}
      <div style={styles.progressHeader}>
        <div style={styles.processingTitleRow}>
          <LoadingSpinner size="sm" />
          <Text style={styles.processingText}>{headerText}</Text>
        </div>
        {progressMessage && (
          <Text style={styles.progressStepLabel}>
            {progressMessage}
          </Text>
        )}
        <div style={styles.progressBarTrack}>
          <div style={styles.progressBarFill} />
        </div>
      </div>

      {/* Live Activity Feed - fills remaining space */}
      <LiveActivityFeed
        taskId={task?.task_id}
        progressMessages={progressMessages}
        isProcessing={hookIsProcessing}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: tokens.spacing[8],
    marginBottom: tokens.spacing[8],
    background: 'var(--color-surface)',
    borderRadius: tokens.borderRadius['2xl'],
    border: '1px solid var(--color-border)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
  },

  // Prompt section
  promptSection: {
    marginBottom: tokens.spacing[8],
    padding: tokens.spacing[6],
    background: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.xl,
    border: '1px solid var(--color-border)',
  },
  promptLabel: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: tokens.colors.primary[400],
    fontWeight: tokens.typography.fontWeight.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: tokens.spacing[3],
  },
  promptText: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text)',
    whiteSpace: 'pre-wrap',
    maxHeight: '200px',
    overflowY: 'auto',
    lineHeight: 1.6,
  },

  // Processing section
  processingSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  progressHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
    marginBottom: tokens.spacing[4],
    padding: tokens.spacing[4],
    background: 'var(--color-bg)',
    borderRadius: tokens.borderRadius.lg,
    border: '1px solid var(--color-border)',
  },
  processingTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
  },
  processingText: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
  },
  progressStepLabel: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    paddingLeft: tokens.spacing[7],
  },
  progressBarTrack: {
    height: '4px',
    borderRadius: '2px',
    backgroundColor: 'var(--color-border)',
    overflow: 'hidden',
    marginTop: tokens.spacing[1],
  },
  progressBarFill: {
    height: '100%',
    width: '40%',
    borderRadius: '2px',
    backgroundColor: tokens.colors.primary[500],
    animation: 'indeterminateSlide 1.5s ease-in-out infinite',
  },
  interruptedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
    marginBottom: tokens.spacing[3],
  },
  interruptedIcon: {
    fontSize: '24px',
  },
  interruptedMessage: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted)',
    lineHeight: 1.5,
  },
  // Actions section
  actionsRow: {
    display: 'flex',
    gap: tokens.spacing[4],
    justifyContent: 'flex-start',
    paddingTop: tokens.spacing[6],
    marginTop: tokens.spacing[6],
    borderTop: '1px solid var(--color-border)',
  },

  // Existing generation status
  existingGenSection: {
    padding: tokens.spacing[6],
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: tokens.borderRadius.lg,
    marginBottom: tokens.spacing[4],
    border: '1px solid var(--color-border)',
    transition: 'all 0.3s ease',
  },
  existingGenHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[3],
    marginBottom: tokens.spacing[4],
  },
  existingGenIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadius.full,
    backgroundColor: 'rgba(120, 183, 180, 0.1)',
    color: tokens.colors.success[500],
    fontSize: '18px',
    fontWeight: 'bold',
  },
  existingGenIconError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadius.full,
    backgroundColor: 'rgba(210, 78, 38, 0.1)',
    color: tokens.colors.error[500],
    fontSize: '18px',
    fontWeight: 'bold',
  },
  existingGenIconMuted: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadius.full,
    backgroundColor: 'var(--color-neutral-800)',
    color: 'var(--color-text-muted)',
    fontSize: '16px',
  },
  existingGenTitle: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
  },
  existingGenTitleError: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: tokens.colors.error[500],
  },
  techStackBadge: {
    fontSize: tokens.typography.fontSize.xs[0],
    backgroundColor: 'var(--color-surface)',
    color: tokens.colors.primary[400],
    padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
    borderRadius: tokens.borderRadius.full,
    fontWeight: tokens.typography.fontWeight.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    border: '1px solid var(--color-border)',
  },
  repoInfoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[3],
    marginBottom: tokens.spacing[4],
  },
  repoLinkInline: {
    color: tokens.colors.primary[400],
    textDecoration: 'none',
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    transition: 'color 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
  },
  cloneCommandInline: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'var(--color-neutral-900)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid var(--color-border)',
    overflow: 'hidden',
    transition: 'border-color 0.2s ease',
  },
  cloneCodeInline: {
    flex: 1,
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
    fontSize: tokens.typography.fontSize.xs[0],
    fontFamily: 'var(--font-mono)',
    color: 'var(--color-text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  copyButtonInline: {
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
    backgroundColor: 'transparent',
    border: 'none',
    borderLeft: '1px solid var(--color-border)',
    color: tokens.colors.primary[400],
    fontSize: tokens.typography.fontSize.xs[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  existingGenActions: {
    display: 'flex',
    gap: tokens.spacing[3],
  },
  errorMessage: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: tokens.colors.error[400],
    marginBottom: tokens.spacing[4],
    padding: tokens.spacing[3],
    backgroundColor: 'rgba(210, 78, 38, 0.1)',
    borderRadius: tokens.borderRadius.md,
    border: '1px solid rgba(210, 78, 38, 0.2)',
  },
};

export default PlanSummary;
